import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { batch, useSignal } from "@preact/signals";
import { nanoid } from "nanoid";
import React, { useMemo } from "react";
import Modal from "../Modal";
import TextInput from "../Form/TextInput";
import ModalFooter from "../ModalFooter";
import Button from "../Button";
import TextArea from "../Form/TextArea";
import Label from "../Form/Label";
import StyledRadioButton from "../Form/StyledRadioButton";
import RadioButtonGroup from "../Form/RadioButtonGroup";
import Loader from "../Loader";
import SelectPill from "../Form/Select/SelectPill";
import ComponentWithData from "../ComponentWithData";
import { Api, Method, getErrorsAsText } from "../../lib/api";
import Select from "../Form/Select";
import { SelectListOption } from "../Form/Select/SelectListOption";
import SelectInput from "../Form/Select/SelectInput";
import StyledSwitch from "../Form/StyledSwitch";
import MessageBox from "../MessageBox";
import { isValidURL, isValidScript } from "../../lib/url";
import URLList from "./URLList";
import style from "./style.module.scss";
import { SelectURLOption } from "./SelectURLOption";
import SelectURLPill from "./SelectURLPill";
export default function ModalTestNow(props) {
    const { isOpen = false, mode, deploy = false, retestID } = props;
    const modalContent = [];
    const testMode = useSignal(mode ? mode : "site");
    const modalState = useSignal("form");
    const modalWidth = useSignal("medium");
    const creatingTests = useSignal(false);
    const modalError = useSignal(false);
    const testError = useSignal("");
    const testSettings = useSignal(null);
    const isLoading = useSignal(true);
    const regions = useSignal([]);
    const browsers = useSignal([]);
    const preSelectedSite = useSignal("");
    const getRegionsData = useMemo(() => Api.fetchJson(`/config/regions/`), []);
    const getBrowsersData = useMemo(() => Api.fetchJson(`/config/test-profiles/`), []);
    const getRetestData = useMemo(() => {
        if (retestID !== undefined) {
            return Api.fetchJson(`/synthetic/tests/${retestID}/settings/`);
        }
        return Promise.resolve(null);
    }, [retestID]);
    if (isLoading.value === true) {
        modalState.value = "loading";
    }
    let hideContentClass = "";
    // Now wait for all of the requests to resolve
    Promise.all([getRegionsData, getBrowsersData, getRetestData])
        .then(([regionsResp, browsersResp, settingsResp]) => {
        batch(() => {
            if (isLoading.value) {
                regions.value = regionsResp.data;
                browsers.value = browsersResp.data;
                modalState.value = "form";
                if (settingsResp !== null) {
                    testSettings.value = settingsResp.data;
                    testMode.value = "url";
                }
                if (window.selected_site_id) {
                    //Site predefined for testing.
                    preSelectedSite.value = String(window.selected_site_id);
                    window.selected_site_id = undefined;
                }
            }
            // Once everything is resolved, update the loading state
            isLoading.value = false;
        });
    })
        .catch(() => {
        modalError.value = true;
        isLoading.value = false;
    });
    const requestCloseModal = () => {
        if (!mode) {
            testMode.value = "site";
        }
        if (props.onClose) {
            props.onClose();
        }
    };
    function fullscreenMode(enabled) {
        if (enabled) {
            modalWidth.value = "large";
        }
        else {
            modalWidth.value = "medium";
        }
    }
    function optionSelected(option) {
        testMode.value = option;
    }
    function createTests(testData) {
        modalError.value = false;
        Api.fetchJson(`/snapshots/`, Method.Post, testData)
            .then((snapshotCreateResponse) => {
            const threeDays = 3 * 24 * 60 * 60;
            if (snapshotCreateResponse.data[0].deploy_id === null) {
                const filters = {
                    date: { r: threeDays },
                    browsers: null,
                    pageLabels: null,
                    regions: null,
                    siteIds: null,
                    testStatus: null,
                    testTrigger: null,
                    testType: null,
                };
                window.location.href = `${window.SPEEDCURVE_CONFIG.accountUrl}/tests/?filters=${JSON.stringify(filters)}`;
            }
            else {
                const filters = {
                    date: { r: threeDays },
                    siteIds: null,
                };
                window.location.href = `${window.SPEEDCURVE_CONFIG.accountUrl}/deployments/?filters=${JSON.stringify(filters)}`;
            }
        })
            .catch((snapshotCreateError) => {
            const statusCode = snapshotCreateError.code;
            const response = snapshotCreateError.response;
            let message;
            if (statusCode && statusCode >= 400 && statusCode < 500) {
                message = snapshotCreateError.message;
                if (response.errors) {
                    message += ` ` + getErrorsAsText(response);
                }
                testError.value = message;
            }
            creatingTests.value = false;
            modalError.value = true;
        });
        //Show the testing started message and hang tight for API response.
        creatingTests.value = true;
    }
    if (testMode.value === "site") {
        modalContent.push(_jsx("div", { children: _jsx(TestSiteForm, { onClose: requestCloseModal, onCreateTests: createTests, deploy: deploy, preSelectedSite: preSelectedSite.value }) }));
    }
    else if (testMode.value === "url") {
        modalContent.push(_jsx("div", { children: _jsx(TestCustomURLForm, { regionsData: regions.value, browsersData: browsers.value, deploy: deploy, onFullScreenMode: fullscreenMode, onClose: requestCloseModal, onCreateTests: createTests, testSettings: testSettings.value }) }));
    }
    if (creatingTests.value) {
        hideContentClass = style["modal-test-now__content--hide"];
    }
    return (_jsxs(Modal, { isOpen: isOpen, onClose: () => requestCloseModal(), id: "test-now-modal", title: "On-Demand Testing", width: modalWidth.value, clickOutsideToCloseDisabled: true, children: [modalState.value === "form" && (_jsxs(_Fragment, { children: [_jsxs("div", { className: hideContentClass, children: [_jsxs(RadioButtonGroup, { layout: "horizontal", title: "What would you like to test?", children: [_jsx(StyledRadioButton, { value: "site", label: "Site", defaultChecked: testMode.value === "site", name: "testtype", onChange: () => optionSelected("site") }), _jsx(StyledRadioButton, { value: "url", label: "Custom URL", defaultChecked: testMode.value === "url", name: "testtype", onChange: () => optionSelected("url") })] }), _jsx("div", { className: style["modal-test-now"], children: modalContent })] }), creatingTests.value === true && (_jsxs("div", { className: style["modal-test-now__loading"], children: [_jsx(Loader, { height: 50 }), _jsx("div", { children: "Please wait, creating your tests." })] })), modalError.value === true && (_jsx(MessageBox, { type: "error", children: testError && testError.value.length
                            ? testError.value
                            : "There was an error when trying to create the tests. Please try again." }))] })), modalState.value === "loading" && (_jsxs("div", { className: style["modal-test-now__loading"], children: [_jsx(Loader, { height: 50 }), _jsx("div", { children: "Please wait, loading testing settings." })] })), modalState.value === "modal-error" && (_jsxs("div", { className: style["modal-test-now__loading"], children: ["That wasn't supposed to happen.", _jsx("br", {}), "There has been an error loading the modal."] }))] }));
}
function TestCustomURLForm(props) {
    var _a, _b, _c, _d;
    const { regionsData, browsersData, onClose, onCreateTests, testSettings, onFullScreenMode } = props;
    //Build defaults for form. If they are retesting an existing test, use those values.
    let defaultURL = { id: nanoid(), urlPath: "" };
    if (testSettings && testSettings.url) {
        defaultURL = {
            id: nanoid(),
            urlPath: testSettings.url,
            script: testSettings.script,
            basicAuth: {
                username: ((_a = testSettings.auth) === null || _a === void 0 ? void 0 : _a.username) ? (_b = testSettings.auth) === null || _b === void 0 ? void 0 : _b.username : "",
                password: ((_c = testSettings.auth) === null || _c === void 0 ? void 0 : _c.password) ? (_d = testSettings.auth) === null || _d === void 0 ? void 0 : _d.password : "",
            },
        };
    }
    const defaultRegion = (testSettings === null || testSettings === void 0 ? void 0 : testSettings.region)
        ? { label: "", value: testSettings.region.region_id }
        : { label: "US West Coast", value: "us-west-1" };
    const defaultBrowser = (testSettings === null || testSettings === void 0 ? void 0 : testSettings.test_profile)
        ? { label: testSettings.test_profile.name, value: testSettings.test_profile.name }
        : { label: "Mobile Fast", value: "Mobile Fast" };
    const defaultNumChecks = (testSettings === null || testSettings === void 0 ? void 0 : testSettings.test_runs) ? testSettings.test_runs : 3;
    const urls = useSignal([defaultURL]);
    const urlErrorIDs = useSignal([false]);
    const scriptErrorIDs = useSignal([false]);
    const selectedRegionOptions = useSignal([defaultRegion]);
    const selectedBrowserOptions = useSignal([defaultBrowser]);
    const selectedNumChecks = useSignal({
        label: defaultNumChecks.toString(),
        value: defaultNumChecks.toString(),
    });
    const deploy = useSignal(props.deploy);
    const deployName = useSignal(undefined);
    const deployDescription = useSignal(undefined);
    const urlError = useSignal("");
    const scriptError = useSignal("");
    const regionError = useSignal("");
    const browserError = useSignal("");
    const deployError = useSignal("");
    function onSubmit(e) {
        e.preventDefault();
        let validData = true;
        urlError.value = "";
        scriptError.value = "";
        //Check at least one URL has been entered.
        if (urls.value.length === 0) {
            urlError.value = "Please enter at least one valid URL to test.";
            validData = false;
        }
        else {
            //Loop though the entered urls and check they are valid.
            const urlErrors = [];
            //Also check is any scripts entered are valid.
            const scriptErrors = [];
            urls.value.forEach((url) => {
                //Check URL is valid.
                if (!isValidURL(url.urlPath)) {
                    urlError.value = "URL entered is not a valid URL.";
                    url.error = true;
                    urlErrors.push(true);
                    validData = false;
                }
                else {
                    url.error = false;
                    urlErrors.push(false);
                }
                //Check script is valid.
                const scriptValidationResult = isValidScript(url.script);
                if (scriptValidationResult.valid) {
                    scriptErrors.push(false);
                }
                else {
                    scriptErrors.push(true);
                    validData = false;
                    scriptError.value = scriptValidationResult.message;
                }
            });
            urlErrorIDs.value = urlErrors;
            scriptErrorIDs.value = scriptErrors;
        }
        if (selectedRegionOptions.value.length === 0) {
            regionError.value = "Please select at least one region to test.";
            validData = false;
        }
        else {
            regionError.value = "";
        }
        if (selectedBrowserOptions.value.length === 0) {
            browserError.value = "Please select at least one browser to test.";
            validData = false;
        }
        else {
            browserError.value = "";
        }
        if (deploy.value === true && (deployName.value === undefined || deployName.value.length === 0)) {
            deployError.value = "Please enter a name for your deploy.";
            validData = false;
        }
        else {
            deployError.value = "";
        }
        //Send the data to the API.
        if (validData) {
            startTests();
        }
    }
    function startTests() {
        //Convert FE data into url objects for BE.
        const URLData = urls.value.map((url) => {
            var _a, _b;
            let urlFull = url.urlPath;
            //Prefix https:// if needed.
            if (urlFull.slice(0, 5) !== "http:" && urlFull.slice(0, 6) !== "https:") {
                urlFull = `https://${urlFull}`;
            }
            return {
                url: urlFull,
                script: url.script,
                auth: {
                    username: (_a = url.basicAuth) === null || _a === void 0 ? void 0 : _a.username,
                    password: (_b = url.basicAuth) === null || _b === void 0 ? void 0 : _b.password,
                },
            };
        });
        //Convert FE data into region objects for BE.
        const [firstSelectedRegionOption] = selectedRegionOptions.value;
        const testRegionsData = firstSelectedRegionOption.value === "all-regions"
            ? regionsData.map((region) => {
                return {
                    region_id: region.region_id,
                };
            })
            : selectedRegionOptions.value.map((region) => {
                return {
                    region_id: region.value,
                };
            });
        //Convert FE data into brwoser objects for BE.
        const [firstSelectedBrowserOption] = selectedBrowserOptions.value;
        const testProfilesData = firstSelectedBrowserOption.value === "all-browsers"
            ? browsersData.map((browser) => {
                return {
                    name: browser.name,
                };
            })
            : selectedBrowserOptions.value.map((browser) => {
                return {
                    name: browser.value,
                };
            });
        const testData = {
            urls: URLData,
            regions: testRegionsData,
            test_profiles: testProfilesData,
            test_runs: selectedNumChecks.value.value,
            deploy: {
                name: deployName.value,
                description: deployDescription.value,
            },
        };
        onCreateTests(testData);
    }
    function addURL() {
        urls.value = [...urls.value, { id: nanoid(), urlPath: "" }];
    }
    function removeURL(urlToRemove) {
        const newURLS = urls.value.filter((urlDetail) => {
            return urlDetail.id !== urlToRemove.id;
        });
        updatedURLData(newURLS);
    }
    function updatedURLData(data) {
        urls.value = data;
    }
    function onRegionUpdate(options) {
        selectedRegionOptions.value = options;
    }
    function onRemoveRegionOption(optionToRemove) {
        selectedRegionOptions.value = selectedRegionOptions.value.filter((option) => {
            return option.value !== optionToRemove.value;
        });
    }
    function onBrowserUpdate(options) {
        selectedBrowserOptions.value = options;
    }
    function onRemoveBrowserOption(optionToRemove) {
        selectedBrowserOptions.value = selectedBrowserOptions.value.filter((option) => {
            return option.value !== optionToRemove.value;
        });
    }
    function onNumChecksUpdate(options) {
        selectedNumChecks.value = options;
    }
    const numChecksData = [];
    for (let i = 1; i < 10; i++) {
        numChecksData.push({
            label: String(i),
            value: String(i),
        });
    }
    //Setup the regions data.
    const regionOptions = regionsData.map((region) => {
        const selectRegionOption = {
            label: region.name,
            value: region.region_id,
        };
        return selectRegionOption;
    });
    //Add an All Regions option to the front of the option list.
    regionOptions.unshift({
        label: "All Regions",
        value: "all-regions",
        toggle: true,
    });
    //Take the browser data and split it into the types of browser.
    const defaultBrowsers = { title: "", options: [] };
    const customBrowsers = { title: "Custom Browsers", options: [] };
    const depreciatedDesktopBrowsers = { title: "Deprecated Desktop Browsers", options: [] };
    const depreciatedMobileBrowsers = { title: "Deprecated Mobile Browsers", options: [] };
    browsersData.forEach((browser) => {
        const selectBrowserOption = {
            label: browser.name,
            value: browser.test_profile_id,
        };
        switch (browser.group) {
            case "Custom":
                customBrowsers.options.push(selectBrowserOption);
                break;
            case "Desktop":
                depreciatedDesktopBrowsers.options.push(selectBrowserOption);
                break;
            case "Mobile":
                depreciatedMobileBrowsers.options.push(selectBrowserOption);
                break;
            default:
                defaultBrowsers.options.push(selectBrowserOption);
        }
    });
    //Create the full list of browser options, always show the default browsers.
    const browserOptions = [defaultBrowsers];
    //Only add the optional browser group types if they have browsers
    if (customBrowsers.options.length > 0) {
        browserOptions.push(customBrowsers);
    }
    if (depreciatedDesktopBrowsers.options.length > 0) {
        browserOptions.push(depreciatedDesktopBrowsers);
    }
    if (depreciatedMobileBrowsers.options.length > 0) {
        browserOptions.push(depreciatedMobileBrowsers);
    }
    //Add an All Browsers option to the front of the option list.
    defaultBrowsers.options.unshift({
        label: "All Browsers",
        value: "all-browsers",
        toggle: true,
    });
    return (_jsxs("form", { id: "customURLForm", onSubmit: (event) => {
            onSubmit(event);
        }, children: [_jsxs("div", { children: [_jsxs("div", { className: style["form-item"], children: [_jsx(Label, { children: "URLs" }), _jsx(URLList, { urlData: urls.value, removeURL: removeURL, addURL: addURL, onFullScreenMode: onFullScreenMode, updateURLData: updatedURLData, error: urlError.value, urlErrors: urlErrorIDs.value, scriptError: scriptError.value, scriptErrors: scriptErrorIDs.value })] }), _jsxs("div", { className: style["form-item"], children: [_jsx(Label, { children: "Regions" }), _jsx(Select, { options: regionOptions, multiple: true, value: selectedRegionOptions.value, onChange: onRegionUpdate, error: regionError.value, renderSelected: (selected, editSelected) => {
                                    return (_jsxs("div", { className: style["modal-test-now__multiselect"], children: [selected.map((option) => {
                                                return (_jsx(SelectPill, { option: option, removable: true, onEdit: editSelected, onRemove: () => onRemoveRegionOption(option) }, option.value));
                                            }), _jsx(Button, { variation: "primary-inline", onClick: editSelected, children: "Add Region" })] }));
                                }, renderOption: (selectOption, selectedState) => {
                                    return _jsx(SelectListOption, { option: selectOption, selected: selectedState, multiple: true });
                                } })] }), _jsxs("div", { className: style["form-item"], children: [_jsx(Label, { children: "Browsers" }), _jsx(Select, { options: browserOptions, multiple: true, value: selectedBrowserOptions.value, onChange: onBrowserUpdate, error: browserError.value, renderSelected: (selected, editSelected) => {
                                    return (_jsxs("div", { className: style["modal-test-now__multiselect"], children: [selected.map((option) => {
                                                return (_jsx(SelectPill, { option: option, removable: true, onEdit: editSelected, onRemove: () => onRemoveBrowserOption(option) }, option.value));
                                            }), _jsx(Button, { variation: "primary-inline", onClick: editSelected, children: "Add Browser" })] }));
                                }, renderOption: (selectOption, selectedState) => {
                                    return _jsx(SelectListOption, { option: selectOption, selected: selectedState, multiple: true });
                                } })] }), _jsxs("div", { className: style["form-item"], children: [_jsx(Label, { children: "Checks" }), _jsx("div", { className: style["form-description"], children: "How many times should each URL be tested." }), _jsx(ComponentWithData, { data: numChecksData, loading: _jsx(Loader, { height: 30 }), error: () => (_jsx(MessageBox, { type: "error", children: "Something went wrong. Please try again later or contact our support team." })), component: (numChecksData) => {
                                    return (_jsx(Select, { options: numChecksData, value: [selectedNumChecks.value], onChange: onNumChecksUpdate, renderSelected: (selected, editSelected) => {
                                            return (_jsx("div", { className: style["modal-test-now__singleselect"], children: selected.map((option) => {
                                                    return (_jsx(SelectPill, { option: option, onEdit: editSelected }, option.value));
                                                }) }));
                                        }, renderOption: (selectOption, selectedState) => {
                                            return (_jsx(SelectListOption, { option: selectOption, selected: selectedState, hideIcon: true }));
                                        } }));
                                } })] }), _jsx("div", { className: style["form-item"], children: _jsx(StyledSwitch, { label: "Group tests as a Deploy", id: "deploy", checked: deploy.value, onChange: () => {
                                deploy.value = !deploy.value;
                            } }, "deploy-toggle") }), _jsx("div", { className: style["form-item"], children: deploy.value === true && (_jsxs(_Fragment, { children: [_jsxs("div", { className: style["form-item"], children: [_jsx(Label, { children: "Deploy Name" }), _jsx(TextInput, { id: "note", name: "note", onBlur: (event) => {
                                                deployName.value = event.currentTarget.value;
                                            }, error: deployError.value.length > 0 }), deployError.value.length > 0 && (_jsx(MessageBox, { type: "error", children: deployError.value }))] }), _jsxs("div", { className: style["form-item"], children: [_jsx(Label, { children: "Deploy Description (Optional)" }), _jsx("div", { children: _jsx(TextArea, { id: "detail", name: "detail", rows: 6, onBlur: (event) => {
                                                    deployDescription.value = event.currentTarget.value;
                                                } }) })] })] })) })] }), _jsxs(ModalFooter, { children: [_jsx(Button, { variation: "primary", onClick: (event) => {
                            onSubmit(event);
                        }, children: "Start Testing" }, "startTest"), _jsx(Button, { onClick: () => onClose(), children: "Cancel" }, "close")] })] }, "custon-url-form"));
}
function TestSiteForm(props) {
    const { onClose, onCreateTests, preSelectedSite } = props;
    const sites = useSignal([]);
    const siteError = useSignal("");
    const urlsError = useSignal("");
    const deployError = useSignal("");
    const isLoading = useSignal(false);
    const siteOptions = window.SPEEDCURVE_CONFIG.siteIds;
    const displayNote = useSignal(false);
    const noteName = useSignal(undefined);
    const noteDescription = useSignal(undefined);
    const deploy = useSignal(props.deploy);
    let defaultSite = [];
    if (preSelectedSite) {
        // Match an existing site
        siteOptions.map((site) => {
            if (site.value === preSelectedSite) {
                defaultSite = [site];
            }
        });
    }
    else if (siteOptions.length === 1) {
        defaultSite = [siteOptions[0]];
    }
    //URLs for each site.
    const urlOptions = [];
    const numSites = siteOptions.length;
    //Add the All URls option.
    const allURLsOption = { label: "All URLs", value: "All URLs", toggle: true };
    const allURLsOptionGroup = {
        options: [allURLsOption],
    };
    urlOptions.push(allURLsOptionGroup);
    const sitesSelected = useSignal(defaultSite);
    const urlsSelected = useSignal([allURLsOption]);
    //Determine which URLs are available based on the currently selected sites. Only show urls for selected sites, if no sites are selected then show all urls.
    //Add the various URLs for each site.
    siteOptions.map((site) => {
        if (numSites > 1) {
            //Check site is part of the selectedSites.
            const siteValid = sitesSelected.value.find((siteSelection) => {
                return siteSelection.value === site.value;
            });
            if (siteValid) {
                const siteSelectData = {
                    title: site.label,
                    options: site.urls,
                };
                urlOptions.push(siteSelectData);
            }
        }
        else {
            const siteSelectData = {
                options: site.urls,
            };
            urlOptions.push(siteSelectData);
        }
    });
    function onSitesUpdated(options) {
        sitesSelected.value = options;
        urlsSelected.value = [allURLsOption];
    }
    function onURLsUpdate(options) {
        urlsSelected.value = options;
    }
    function onRemoveURLOption(optionToRemove) {
        urlsSelected.value = urlsSelected.value.filter((option) => {
            return option.value !== optionToRemove.value;
        });
    }
    function onSubmit(event) {
        event.preventDefault();
        //Do FE validation.
        let validData = true;
        if (sitesSelected.value.length === 0) {
            siteError.value = "Please select a site to test";
            validData = false;
        }
        else {
            siteError.value = "";
        }
        if (urlsSelected.value.length === 0) {
            urlsError.value = "Please select a URL to test";
            validData = false;
        }
        else {
            urlsError.value = "";
        }
        if (deploy.value === true && noteName.value === undefined) {
            deployError.value = "Please enter a deploy name.";
            validData = false;
        }
        else {
            deployError.value = "";
        }
        if (validData) {
            //Send the data to the API.
            startTests();
        }
    }
    function startTests() {
        //Convert FE data into sites objects for BE.
        const sitesData = sitesSelected.value.map((site) => {
            const urlsData = [];
            urlsSelected.value.map((url) => {
                siteOptions.map((siteOption) => {
                    site.urls.map((urlOption) => {
                        if (siteOption.value === site.value && urlOption.value === url.value) {
                            urlsData.push({
                                url_id: url.value,
                            });
                        }
                    });
                });
            });
            return {
                site_id: site.value,
                urls: urlsData,
            };
        });
        if (deploy.value === true) {
            //Setup deploy testing.
            const testData = {
                sites: sitesData,
                deploy: {
                    name: noteName.value,
                    description: noteDescription.value,
                },
            };
            onCreateTests(testData);
        }
        else {
            //Setup site testing.
            const testData = {
                sites: sitesData,
                note: {
                    name: noteName.value,
                    description: noteDescription.value,
                },
            };
            onCreateTests(testData);
        }
    }
    let showNoteInputs = false;
    if (displayNote.value || deploy.value === true) {
        showNoteInputs = true;
    }
    return (_jsxs("form", { onSubmit: onSubmit, children: [isLoading.value ? (_jsx("div", { className: style["modal-loading"], children: _jsx(Loader, {}) })) : null, _jsx("input", { type: "hidden", name: "site_id", value: sites.value.join(",") }), _jsxs("div", { className: style["form-item"], children: [_jsx(Label, { children: "Site to be tested" }), _jsx(Select, { options: siteOptions, value: sitesSelected.value, multiple: true, width: 400, onChange: onSitesUpdated, renderSelected: (selected, editSelected) => {
                            return _jsx(SelectInput, { options: selected, onEdit: editSelected });
                        }, renderOption: (selectOption, selectedState) => {
                            return _jsx(SelectListOption, { option: selectOption, selected: selectedState, multiple: true });
                        }, error: siteError.value })] }), _jsxs("div", { className: style["form-item"], children: [_jsx(Label, { children: "URLs to be tested" }), _jsx(Select, { options: urlOptions, multiple: true, value: urlsSelected.value, onChange: onURLsUpdate, error: urlsError.value, width: 700, renderSelected: (selected, editSelected) => {
                            return (_jsxs("div", { className: style["modal-test-now__multiselect"], children: [selected.map((option) => {
                                        return (_jsx(SelectURLPill, { option: option, removable: true, onEdit: editSelected, onRemove: () => onRemoveURLOption(option) }, option.value));
                                    }), _jsx(Button, { variation: "primary-inline", onClick: editSelected, children: "Edit URLs" })] }));
                        }, renderOption: (selectOption, selectedState) => {
                            return (_jsx(SelectURLOption, { option: selectOption, selected: selectedState, multiple: true }));
                        } })] }), _jsx("div", { className: style["form-item"], children: _jsx(StyledSwitch, { label: "Group tests as a Deploy", id: "deploy", checked: deploy.value, onChange: () => {
                        deploy.value = !deploy.value;
                    } }, "deploy-toggle") }), showNoteInputs === true && (_jsxs(_Fragment, { children: [_jsxs("div", { className: style["form-item"], children: [_jsx(Label, { children: deploy.value ? "Deploy Name" : "Name (Optional)" }), _jsx(TextInput, { id: "note", name: "note", onInput: (event) => {
                                    noteName.value = event.currentTarget.value;
                                }, error: deployError.value.length > 0 }), deployError.value.length > 0 && _jsx(MessageBox, { type: "error", children: deployError.value })] }), _jsxs("div", { className: style["form-item"], children: [_jsxs(Label, { children: [deploy.value ? "Deploy Description" : "Description", " (Optional)"] }), _jsx("div", { children: _jsx(TextArea, { id: "detail", name: "detail", rows: 6, onInput: (event) => {
                                        noteDescription.value = event.currentTarget.value;
                                    } }) })] })] })), showNoteInputs === false && (_jsx("div", { className: style["form-item"], children: _jsx(Button, { className: style["add-note-button"], onClick: () => {
                        displayNote.value = true;
                    }, variation: "primary-inline", children: "Add Note" }) })), _jsxs(ModalFooter, { children: [_jsx(Button, { variation: "primary", className: "qa-test-deploy", disabled: isLoading.value, type: "submit", children: "Start Testing" }), _jsx(Button, { onClick: onClose, children: "Cancel" })] })] }));
}
